/* My Jobs dashboard — types matching GET /api/v1/my-jobs, and the small amount of
 * screen logic. Which step a test is at is decided by the API (MyJobStepRules). */
import { api } from '../../../api/client';
import { NEW_RECORD, navigateToPage } from '../../../layouts/navigateTo';

export const MY_JOBS_PAGE = 'pa-lab-myjobs';
export const MY_JOBS_LABEL = 'My Jobs';
export const SAMPLE_REGISTRATION_PAGE = 'sampreg';
const PLM_PAGE = 'plm';

/** One row of the list: a test of a job, or the job itself until it has tests. */
export interface MyJobRow {
  rowId: string;
  jobOid: string;
  jobId: string;
  client: string | null;
  projectName: string | null;
  receivedDate: string | null;
  dueDate: string | null;
  testMethodOid: string | null;
  test: string | null;
  method: string | null;
  sampleCount: number;
  stepIndex: number;
  stepName: string;
  samplesAtStep: number;
  status: string;
  missing: string[];
  qcBatchIds: string[];
  nextTitle: string;
  nextDescription: string;
  nextAction: string;
  targetPage: string;
  targetLabel: string;
  targetBatchOid: string | null;
  opensResultEntry: boolean;
  newBatch: MyJobNewBatch | null;
}

/** What opens the QC Batch form on a new batch: the test, and this job's samples waiting for it. */
export interface MyJobNewBatch {
  testMethodOid: string;
  test: string | null;
  method: string | null;
  matrix: string | null;
  templateId: number | null;
  sampleOids: string[];
}

export interface MyJobStepCount { stepIndex: number; stepName: string; samples: number; jobs: number; }

export interface MyJobsDashboard {
  jobs: MyJobRow[];
  openJobs: number;
  steps: MyJobStepCount[];
  samplesInProgress: number;
  deliveredLast7Days: number;
}

export async function loadMyJobs(): Promise<MyJobsDashboard> {
  const res = await api.get<{ data: MyJobsDashboard }>('/api/v1/my-jobs');
  return res.data.data;
}

/**
 * A lab reviews results either job by job or batch by batch, and Data Review Settings
 * shows it only the forms it uses. The API names the job-level form by the test's
 * status, so when that one is switched off the batch-level twin holds the same work.
 */
const REVIEW_TWIN: Record<string, { page: string; label: string }> = {
  rval: { page: 'sdmsval', label: 'SDMS Result Validation' },
  rappr: { page: 'sdmsappr', label: 'SDMS Result Approval' },
  sdmsval: { page: 'rval', label: 'Result Validation' },
  sdmsappr: { page: 'rappr', label: 'Result Approval' },
};

type CanSee = (id: string, label?: string) => boolean;

/** The form this user can open for the row's current step, or null when the step is with someone else. */
export function openableTarget(job: MyJobRow, canSee?: CanSee): { page: string; label: string } | null {
  const target = { page: job.targetPage, label: job.targetLabel };
  if (!canSee || canSee(target.page, target.label)) return target;
  const twin = REVIEW_TWIN[job.targetPage];
  return twin && canSee(twin.page, twin.label) ? twin : null;
}

/** The status to show: a job whose next form the user cannot open is waiting on someone else. */
export function displayStatus(job: MyJobRow, canOpen: boolean): string {
  return canOpen ? job.status : 'Waiting';
}

/** "1 sample", "3 samples". */
export const sampleCountText = (n: number) => (n === 1 ? '1 sample' : `${n} samples`);

/** Search box: Job ID, test, method, QC batch ID, client or project, within the login user's own jobs. */
export function matchesSearch(job: MyJobRow, term: string): boolean {
  const t = term.trim().toLowerCase();
  if (!t) return true;
  return [job.jobId, job.test, job.method, job.client, job.projectName, ...job.qcBatchIds]
    .some((v) => (v ?? '').toLowerCase().includes(t));
}

/** What the form needs to open on this job, beyond its Job ID. */
export function resumeContext(job: MyJobRow): Record<string, string> {
  const context: Record<string, string> = {};
  // Sample Registration: 0 = job details, 1 = samples.
  if (job.targetPage === SAMPLE_REGISTRATION_PAGE) context.step = job.stepName === 'Log In' ? '1' : '0';
  if (job.targetBatchOid) context.batchOid = job.targetBatchOid;
  if (job.opensResultEntry) context.resultEntry = '1';
  if (job.targetPage === PLM_PAGE) context.mode = 'validate';
  if (job.newBatch) {
    context.jobOid = job.jobOid;
    context.newBatch = JSON.stringify(job.newBatch);
  }
  return context;
}

const backToMyJobs = (detail?: string) => ({ page: MY_JOBS_PAGE, label: MY_JOBS_LABEL, detail });

/** Opens the form where this job is pending; closing that form comes back here. */
export function resumeJob(job: MyJobRow, page: string = job.targetPage): void {
  navigateToPage(page, job.jobId, {
    context: resumeContext(job),
    returnTo: backToMyJobs(`Job ${job.jobId}${job.test ? ` · ${job.test}` : ''} · ${job.stepName}`),
  });
}

/** Starts a new job in Sample Registration. */
export function startNewJob(): void {
  navigateToPage(SAMPLE_REGISTRATION_PAGE, NEW_RECORD, { returnTo: backToMyJobs('New job') });
}
